import type {
  CursorPaginatedResponse,
  RentCar,
  RentCarAvailableData,
  RentCarBookingHistory,
  RentCarBookingHistoryParams,
  RentCarParams,
} from '@/libs/interfaces'

import { mockRentCarBookingHistory, mockRentCars } from '../fixtures/rentCar'
import { delay, matchesSearch, nowISO, paginate } from '../helpers'
import { createEntityStore } from '../store'

const rentCarStore = createEntityStore<RentCar, 'rentCarRegNum'>(mockRentCars, 'rentCarRegNum')
const rentCarBookingHistoryStore = createEntityStore<RentCarBookingHistory, 'rentCarBookingID'>(
  mockRentCarBookingHistory,
  'rentCarBookingID',
)

const filterRentCars = (params: RentCarParams): RentCar[] =>
  rentCarStore
    .find((rentCar) => rentCar.storeID === params.storeID)
    .filter((rentCar) => (params.showInactiveRentCars ? true : rentCar.rentCarActive))
    .filter((rentCar) => matchesSearch(rentCar, params.search, ['rentCarBrand', 'rentCarModel']))

const filterBookingHistory = (params: RentCarBookingHistoryParams): RentCarBookingHistory[] =>
  rentCarBookingHistoryStore
    .find((booking) => booking.storeID === params.storeID)
    .filter((booking) =>
      params.bookingStatus ? booking.bookingStatus === params.bookingStatus : true,
    )
    .filter((booking) => (params.rentCar ? booking.rentCarRegNum === params.rentCar : true))
    .filter((booking) => (params.bookedBy ? booking.bookedBy === params.bookedBy : true))

export const rentCarAPIMock = {
  create: async (body: Partial<RentCar>): Promise<Partial<RentCar>> => {
    const rentCar: RentCar = {
      rentCarActive: true,
      rentCarStatus: 'Active',
      ...body,
      createdAt: nowISO(),
      updatedAt: nowISO(),
    }
    rentCarStore.insert(rentCar)
    return delay(rentCar)
  },

  delete: async (params: Partial<RentCar>): Promise<RentCar> => {
    const rentCar = params.rentCarRegNum ? rentCarStore.getById(params.rentCarRegNum) : undefined
    if (params.rentCarRegNum) rentCarStore.remove(params.rentCarRegNum)
    return delay(rentCar ?? {})
  },

  getAvailableRentCars: async (params: RentCarParams): Promise<RentCarAvailableData> => {
    const storeCars = filterRentCars({ ...params, showInactiveRentCars: true })
    const bookedRegNums = new Set(
      rentCarBookingHistoryStore
        .find(
          (booking) =>
            booking.storeID === params.storeID &&
            (booking.bookingStatus === 'created' || booking.bookingStatus === 'pickedup'),
        )
        .map((booking) => booking.rentCarRegNum),
    )
    return delay({
      available: storeCars.filter(
        (rentCar) => rentCar.rentCarActive && !bookedRegNums.has(rentCar.rentCarRegNum),
      ),
      unavailable: storeCars.filter(
        (rentCar) => !rentCar.rentCarActive || bookedRegNums.has(rentCar.rentCarRegNum),
      ),
    })
  },

  getByRegNum: async (params: Partial<RentCar>): Promise<RentCar> =>
    delay(rentCarStore.getById(params.rentCarRegNum ?? '') ?? {}),

  listCursor: async (params: RentCarParams): Promise<CursorPaginatedResponse<RentCar>> =>
    delay(paginate(filterRentCars(params), params)),

  listHistoryCursor: async (
    params: RentCarBookingHistoryParams,
  ): Promise<CursorPaginatedResponse<RentCarBookingHistory>> =>
    delay(paginate(filterBookingHistory(params), params)),

  update: async (payload: Partial<RentCar>): Promise<RentCar> => {
    const updated = payload.rentCarRegNum
      ? rentCarStore.update(payload.rentCarRegNum, { ...payload, updatedAt: nowISO() })
      : undefined
    return delay(updated ?? {})
  },
}
